import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import axios from "axios";
import AllBaiseLogo from "@/components/AllBaiseLogo";
import HubSelector from "@/components/HubSelector";
import {
  FileText, Monitor, Users, GraduationCap, CheckSquare,
  BookOpen, Bot, Zap, Shield, Globe, Radio, Lightbulb,
  ArrowRight, ChevronRight, Brain, Eye, Lock, Gift,
  Play, Target, Network, Cpu, MapPin, Smartphone, User as UserIcon,
  Building2, Check, X as XIcon, Briefcase
} from "lucide-react";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

// Country list with codes
const COUNTRIES = [
  { code: "ZA", name: "South Africa" }, { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" }, { code: "AU", name: "Australia" },
  { code: "CA", name: "Canada" }, { code: "DE", name: "Germany" },
  { code: "FR", name: "France" }, { code: "IN", name: "India" },
  { code: "JP", name: "Japan" }, { code: "BR", name: "Brazil" },
  { code: "NG", name: "Nigeria" }, { code: "KE", name: "Kenya" },
  { code: "GH", name: "Ghana" }, { code: "EG", name: "Egypt" },
  { code: "AE", name: "UAE" }, { code: "SA", name: "Saudi Arabia" },
  { code: "SG", name: "Singapore" }, { code: "CN", name: "China" },
  { code: "MX", name: "Mexico" }, { code: "IT", name: "Italy" },
  { code: "ES", name: "Spain" }, { code: "NL", name: "Netherlands" },
  { code: "CH", name: "Switzerland" }, { code: "SE", name: "Sweden" },
  { code: "PL", name: "Poland" }, { code: "NZ", name: "New Zealand" },
];

export default function Landing() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ name: "", email: "", password: "", username: "", country: "" });
  const [registerType, setRegisterType] = useState("personal"); // personal | corporate
  const [corpData, setCorpData] = useState({ orgName: "", regNumber: "", subdomain: "", adminName: "", adminEmail: "", adminPassword: "" });
  const [corpSubdomainAvailable, setCorpSubdomainAvailable] = useState(null);
  const [corpPlans, setCorpPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("plan_corp_basic");
  const [selectedHubs, setSelectedHubs] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, error: null });
  const [showTotpInput, setShowTotpInput] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [verifyingTotp, setVerifyingTotp] = useState(false);
  const sortedCountries = [...COUNTRIES].sort((a, b) => a.name.localeCompare(b.name));

  const hubStyles = {
    blue: { panel: "bg-blue-500/15", icon: "text-blue-400" },
    red: { panel: "bg-red-500/15", icon: "text-red-400" },
    emerald: { panel: "bg-emerald-500/15", icon: "text-emerald-400" },
    amber: { panel: "bg-amber-500/15", icon: "text-amber-400" },
    purple: { panel: "bg-purple-500/15", icon: "text-purple-400" },
    cyan: { panel: "bg-cyan-500/15", icon: "text-cyan-400" },
    rose: { panel: "bg-rose-500/15", icon: "text-rose-400" },
    yellow: { panel: "bg-yellow-500/15", icon: "text-yellow-400" },
    teal: { panel: "bg-teal-500/15", icon: "text-teal-400" },
  };

  useEffect(() => {
    if (auth.user) navigate("/");
  }, [auth.user, navigate]);

  // Fetch corporate plans when registration tab is corporate
  useEffect(() => {
    if (registerType === "corporate" && corpPlans.length === 0) {
      setLoadingPlans(true);
      axios.get(`${API}/tenant/plans?type=CORPORATE`)
        .then(r => {
          setCorpPlans(r.data.plans || []);
          if (r.data.plans?.length > 0) {
            setSelectedPlanId(r.data.plans[0].id);
          }
        })
        .catch(e => console.error("Failed to load plans:", e))
        .finally(() => setLoadingPlans(false));
    }
  }, [registerType]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginData, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
      });

      // Check if 2FA is required
      if (res.data.requires_2fa) {
        setTempToken(res.data.temp_token);
        setShowTotpInput(true);
        setTotpCode("");
        setIsLoading(false);
        return;
      }

      localStorage.setItem("token", res.data.token);
      auth.setAuthUser(res.data.user, res.data.token);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail || "Login failed";
      if (detail && detail.includes("verify your email")) {
        toast.error(detail);
        const resend = window.confirm("Would you like to resend the verification email?");
        if (resend && loginData.email) {
          try {
            await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(loginData.email)}`);
            toast.success("Verification email resent!");
          } catch { toast.error("Could not resend verification email"); }
        }
      } else {
        toast.error(detail);
      }
    }
    setIsLoading(false);
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length < 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    setVerifyingTotp(true);
    try {
      const res = await axios.post(`${API}/auth/login/2fa`, {
        temp_token: tempToken,
        code: totpCode
      }, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
      });
      localStorage.setItem("token", res.data.token);
      auth.setAuthUser(res.data.user, res.data.token);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    }
    setVerifyingTotp(false);
  };

  const checkUsername = async (username) => {
    if (!username || username.length < 3) {
      setUsernameStatus({ checking: false, available: null, error: "Username must be at least 3 characters" });
      return;
    }
    // Validate alphanumeric only (no underscores per user request)
    if (!/^[a-z0-9]+$/i.test(username)) {
      setUsernameStatus({ checking: false, available: false, error: "Username must be alphanumeric only" });
      return;
    }
    if (username.length > 20) {
      setUsernameStatus({ checking: false, available: false, error: "Username must be 20 characters or less" });
      return;
    }
    
    setUsernameStatus({ checking: true, available: null, error: null });
    try {
      const res = await axios.get(`${API}/auth/check-username/${username.toLowerCase()}`);
      setUsernameStatus({ 
        checking: false, 
        available: res.data.available, 
        error: res.data.available ? null : "Username is already taken" 
      });
    } catch (err) {
      setUsernameStatus({ checking: false, available: null, error: "Could not check username" });
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate username before registration
    if (!registerData.username || registerData.username.length < 3) {
      toast.error("Please enter a valid username (at least 3 characters)");
      return;
    }
    if (!/^[a-z0-9]+$/i.test(registerData.username)) {
      toast.error("Username must be alphanumeric only (letters and numbers)");
      return;
    }
    if (usernameStatus.available === false) {
      toast.error("Please choose an available username");
      return;
    }
    
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        ...registerData,
        username: registerData.username.toLowerCase(),
        account_type: registerType
      }, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
      });

      // Check if email verification is required
      if (res.data.needs_verification) {
        toast.success("Account created! Please check your email to verify your account.");
        // Switch to login tab so user can log in after verification
        registerData.email = res.data.email;
        setLoginData({ email: res.data.email, password: registerData.password });
        document.querySelector('[data-value="login"]')?.click();
        return;
      }

      localStorage.setItem("token", res.data.token);
      auth.setAuthUser(res.data.user, res.data.token);
      toast.success("Account created successfully!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setIsLoading(false);
  };

  // ── Corporate Registration ──
  const handleCorporateRegister = async (e) => {
    e.preventDefault();
    if (!corpData.subdomain || corpData.subdomain.length < 2) {
      toast.error("Please enter a subdomain (at least 2 characters)");
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/tenant/register/corporate`, {
        corporate_name: corpData.orgName,
        registration_number: corpData.regNumber,
        subdomain: corpData.subdomain,
        admin_name: corpData.adminName,
        admin_email: corpData.adminEmail,
        admin_password: corpData.adminPassword,
        plan_id: selectedPlanId,
        selected_hubs: selectedHubs,
      }, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success(`Corporate account created! Login as ${corpData.adminEmail}`);
      // Switch to login tab
      setLoginData({ email: corpData.adminEmail, password: corpData.adminPassword });
      document.querySelector('[data-value="login"]')?.click();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Corporate registration failed");
    }
    setIsLoading(false);
  };

  const hubs = [
    { icon: FileText, name: "Content Hub", desc: "AI document analysis & transcription", color: "blue" },
    { icon: Monitor, name: "Monitoring Centre", desc: "Live surveillance & alerts", color: "red" },
    { icon: Users, name: "HR Hub", desc: "Performance & skills audit", color: "emerald" },
    { icon: GraduationCap, name: "Learning Hub", desc: "LMS for development", color: "amber" },
    { icon: CheckSquare, name: "Tasks Hub", desc: "Calendar task management", color: "purple" },
    { icon: BookOpen, name: "Knowledge Centre", desc: "SOPs & documentation", color: "cyan" },
    { icon: Radio, name: "Media Intelligence", desc: "OSINT & monitoring", color: "rose" },
    { icon: Lightbulb, name: "Innovation Centre", desc: "AI idea pipeline", color: "yellow" },
    { icon: Gift, name: "Rewards Centre", desc: "Offers, credits & incentives", color: "teal" },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute top-0 left-0 w-full h-full">
          {/* Grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:100px_100px]" />
          {/* Glowing orbs */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s" }} />
        </div>
      </div>

      {/* Navigation */}
      <nav className="relative z-50 border-b border-white/10 backdrop-blur-xl bg-slate-950/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <AllBaiseLogo size={38} />
              <div className="absolute -inset-1 bg-blue-500/20 blur-sm -z-10" />
            </div>
            <div>
              <Link to="/" className="font-['Barlow_Condensed'] text-xl font-bold tracking-wider text-white hover:text-blue-300 transition-colors">
                AllBaise
              </Link>
              <div className="h-0.5 w-full bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700" />
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-slate-400">
            <span className="hover:text-white cursor-pointer transition-colors">Features</span>
            <span className="hover:text-white cursor-pointer transition-colors">Capabilities</span>
            <span className="hover:text-white cursor-pointer transition-colors">Security</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
            {/* Left: Content */}
            <div className="space-y-8">
              {/* Badge */}
              {/* Main Headline */}
              <div className="space-y-4">
                <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight">
                  <span className="text-white">WELCOME TO THE</span>
                  <br />
                  <span className="relative inline-block">
                    <span className="bg-gradient-to-r from-red-400 via-blue-400 to-emerald-400 bg-clip-text text-transparent">
                      AllBaise
                    </span>
                    <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-blue-500 to-emerald-500" />
                  </span>
                </h1>
                <p className="text-xl md:text-2xl text-slate-400 font-light max-w-lg">
                  Office Bespoke AI Scalable Engine (AllBaise)
                </p>
              </div>

              {/* Description */}
                <p className="text-slate-400 text-base md:text-lg leading-relaxed max-w-lg">
                  An expandable platform unifying document management, communication tools, analytics, collaboration, learning management system and innovation management powered by AI-insights.
                </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 md:gap-6">
                <div className="space-y-1">
                  <p className="text-2xl md:text-3xl font-bold text-white">9</p>
                  <p className="text-xs md:text-sm text-slate-500">Integrated Hubs</p>
                </div>
                <div className="space-y-1">
                  <p className="text-2xl md:text-3xl font-bold text-white">AI</p>
                  <p className="text-xs md:text-sm text-slate-500">Powered Bots</p>
                </div>
                <div className="space-y-1">
                  <p className="text-3xl font-bold text-white">24/7</p>
                  <p className="text-sm text-slate-500">Monitoring</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 pt-4">
                <Button
                  onClick={() => document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-none shadow-lg shadow-red-500/30 transition-all hover:shadow-xl hover:shadow-red-500/40 w-full sm:w-auto justify-center"
                  data-testid="get-started-btn"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-none backdrop-blur-sm w-full sm:w-auto justify-center"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Watch Demo
                </Button>
              </div>
            </div>

            {/* Right: Auth Form */}
            <div id="auth-section" className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-red-500/20 via-blue-500/20 to-emerald-500/20 blur-2xl opacity-50" />
              <div className="relative bg-slate-900/80 backdrop-blur-xl border border-white/10 p-4 sm:p-8 shadow-2xl">
                <div className="text-center mb-8">
                  <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white mb-2">
                    ACCESS THE PLATFORM
                  </h2>
                  <p className="text-slate-400 text-sm">
                    Sign in or create an account to continue
                  </p>
                </div>

                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="w-full bg-slate-800/50 border border-white/10 rounded-none p-1 mb-6">
                    <TabsTrigger 
                      value="login" 
                      className="flex-1 rounded-none data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400"
                      data-testid="login-tab"
                    >
                      Sign In
                    </TabsTrigger>
                    <TabsTrigger 
                      value="register" 
                      className="flex-1 rounded-none data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400"
                      data-testid="register-tab"
                    >
                      Register
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="login">
                    {showTotpInput ? (
                      <form onSubmit={handleTotpSubmit} className="space-y-4">
                        <div className="p-3 bg-slate-800/30 rounded-lg border border-white/10">
                          <div className="flex items-center gap-2 mb-2">
                            <Smartphone className="w-4 h-4 text-red-400" />
                            <p className="text-sm text-slate-300 font-medium">Two-Factor Authentication</p>
                          </div>
                          <p className="text-xs text-slate-400">
                            Enter the 6-digit code from your authenticator app.
                          </p>
                        </div>
                        <div>
                          <Label className="text-slate-400 text-sm">Authentication Code</Label>
                          <Input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="000000"
                            value={totpCode}
                            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-500 rounded-none mt-1 focus:border-red-500 text-center text-lg tracking-widest"
                            autoFocus
                            required
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={verifyingTotp || totpCode.length < 6}
                          className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-none py-5 shadow-lg shadow-red-500/20"
                        >
                          {verifyingTotp ? "Verifying..." : "Verify Code"}
                          <Shield className="w-4 h-4 ml-1" />
                        </Button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowTotpInput(false);
                            setTempToken("");
                            setTotpCode("");
                          }}
                          className="w-full text-center text-sm text-slate-500 hover:text-slate-300 transition-colors"
                        >
                          Back to login
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                          <Label className="text-slate-400 text-sm">Email</Label>
                          <Input
                            type="email"
                            placeholder="you@company.com"
                            value={loginData.email}
                            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                            className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-500 rounded-none mt-1 focus:border-red-500"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-sm">Password</Label>
                          <Input
                            type="password"
                            placeholder="Enter your password"
                            value={loginData.password}
                            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                            className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-500 rounded-none mt-1 focus:border-red-500"
                            required
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={isLoading}
                          className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-none py-5 shadow-lg shadow-red-500/20"
                          data-testid="login-submit-btn"
                        >
                          {isLoading ? "Signing in..." : "Sign In"}
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </form>
                    )}
                  </TabsContent>

                  <TabsContent value="register">
                    {/* Account Type Selector */}
                    <div className="flex gap-2 mb-4">
                      <button type="button" onClick={() => setRegisterType("personal")}
                        className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg border transition-all ${
                          registerType === "personal"
                            ? "bg-blue-600 border-blue-500 text-white"
                            : "bg-slate-800/50 border-white/10 text-slate-400 hover:border-blue-400"
                        }`}>
                        <UserIcon className="w-4 h-4 mx-auto mb-1" />
                        Personal
                      </button>
                      <button type="button" onClick={() => setRegisterType("corporate")}
                        className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg border transition-all ${
                          registerType === "corporate"
                            ? "bg-violet-600 border-violet-500 text-white"
                            : "bg-slate-800/50 border-white/10 text-slate-400 hover:border-violet-400"
                        }`}>
                        <Building2 className="w-4 h-4 mx-auto mb-1" />
                        Corporate
                      </button>
                    </div>

                    {registerType === "personal" ? (
                    <form onSubmit={handleRegister} className="space-y-4">
                      <div>
                        <Label className="text-slate-400 text-sm">Name</Label>
                        <Input
                          placeholder="Your name"
                          value={registerData.name}
                          onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                          className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-500 rounded-none mt-1 focus:border-red-500"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm">Username</Label>
                        <div className="relative">
                          <Input
                            placeholder="Choose a unique username"
                            value={registerData.username}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^a-zA-Z0-9]/g, ''); // Strip non-alphanumeric
                              setRegisterData({ ...registerData, username: val });
                              if (val.length >= 3) {
                                checkUsername(val);
                              } else {
                                setUsernameStatus({ checking: false, available: null, error: null });
                              }
                            }}
                            className={`bg-slate-800/50 border-white/10 text-white placeholder:text-slate-500 rounded-none mt-1 pr-10 ${
                              usernameStatus.available === true ? 'border-green-500 focus:border-green-500' : 
                              usernameStatus.available === false ? 'border-red-500 focus:border-red-500' : 'focus:border-red-500'
                            }`}
                            data-testid="register-username"
                            required
                          />
                          {usernameStatus.checking && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5">
                              <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                          )}
                          {!usernameStatus.checking && usernameStatus.available === true && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-green-500">✓</div>
                          )}
                          {!usernameStatus.checking && usernameStatus.available === false && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-red-500">✗</div>
                          )}
                        </div>
                        {usernameStatus.error && (
                          <p className="text-red-400 text-xs mt-1">{usernameStatus.error}</p>
                        )}
                        {usernameStatus.available === true && (
                          <p className="text-green-400 text-xs mt-1">Username is available!</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm">Email</Label>
                        <Input
                          type="email"
                          placeholder="you@company.com"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-500 rounded-none mt-1 focus:border-red-500"
                          data-testid="register-email"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm">Password</Label>
                        <Input
                          type="password"
                          placeholder="Create a password"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-500 rounded-none mt-1 focus:border-red-500"
                          data-testid="register-password"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          Country
                        </Label>
                        <Select
                          value={registerData.country}
                          onValueChange={(value) => setRegisterData({ ...registerData, country: value })}
                        >
                          <SelectTrigger 
                            className="bg-slate-800/50 border-white/10 text-white rounded-none mt-1 focus:border-red-500"
                            data-testid="register-country"
                          >
                            <SelectValue placeholder="Select your country" />
                          </SelectTrigger>
                          <SelectContent>
                          {sortedCountries.map((c) => (
                              <SelectItem key={c.code} value={c.code}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        type="submit" 
                        disabled={isLoading || usernameStatus.available === false}
                        className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-none py-5 shadow-lg shadow-red-500/20 disabled:opacity-50"
                        data-testid="register-submit-btn"
                      >
                        {isLoading ? "Creating account..." : "Create Account"}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </form>
                    ) : (
                    <form onSubmit={handleCorporateRegister} className="space-y-4">
                      <div>
                        <Label className="text-slate-400 text-sm">Organization Name</Label>
                        <Input placeholder="e.g. BMW Group" value={corpData.orgName} onChange={e => setCorpData({...corpData, orgName: e.target.value})}
                          className="bg-slate-800/50 border-white/10 text-white rounded-none mt-1 focus:border-violet-500" required />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm">Registration Number</Label>
                        <Input placeholder="Company registration number" value={corpData.regNumber} onChange={e => setCorpData({...corpData, regNumber: e.target.value})}
                          className="bg-slate-800/50 border-white/10 text-white rounded-none mt-1 focus:border-violet-500" />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm">Subdomain</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input placeholder="your-company" value={corpData.subdomain} onChange={e => {
                            const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                            setCorpData({...corpData, subdomain: val});
                            if (val.length >= 2) {
                              axios.get(`${API}/tenant/check-subdomain/${val}`, { headers: getHeaders() }).then(r => setCorpSubdomainAvailable(r.data.available)).catch(() => {});
                            } else { setCorpSubdomainAvailable(null); }
                          }}
                            className="flex-1 bg-slate-800/50 border-white/10 text-white rounded-none focus:border-violet-500" />
                          <span className="text-xs text-slate-500 whitespace-nowrap">.allbaise.com</span>
                          {corpSubdomainAvailable === true && <Check className="w-4 h-4 text-green-400 shrink-0" />}
                          {corpSubdomainAvailable === false && <XIcon className="w-4 h-4 text-red-400 shrink-0" />}
                        </div>
                      </div>
                      {/* Plan Selection */}
                      <div className="h-px bg-white/10" />
                      <p className="text-xs text-slate-500">Select Plan</p>
                      {loadingPlans ? (
                        <p className="text-xs text-slate-500">Loading plans...</p>
                      ) : corpPlans.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2">
                          {corpPlans.map(plan => {
                            const isFree = plan.price_monthly === 0 && plan.price_yearly === 0;
                            const priceDisplay = isFree ? "Free" :
                              plan.billing_period === "monthly" ? `R${(plan.price_monthly/100).toFixed(2)}/mo` :
                              plan.billing_period === "yearly" ? `R${(plan.price_yearly/100).toFixed(2)}/yr` :
                              plan.billing_period === "per_user" ? `R${(plan.price_per_user_monthly/100).toFixed(2)}/user` : "";
                            const hubCount = Object.values(plan.hubs || {}).filter(Boolean).length;
                            return (
                              <button type="button" key={plan.id}
                                onClick={() => { setSelectedPlanId(plan.id); setSelectedHubs(null); }}
                                className={`text-left p-2 rounded border transition-all text-xs ${
                                  selectedPlanId === plan.id
                                    ? "bg-violet-600/20 border-violet-500 text-white"
                                    : "bg-slate-800/50 border-white/10 text-slate-400 hover:border-violet-400"
                                }`}>
                                <div className="font-medium text-sm">{plan.name}</div>
                                <div className="text-violet-300 font-semibold mt-0.5">{priceDisplay}</div>
                                <div className="text-slate-500 mt-0.5">{hubCount} hubs</div>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500">Default: Corporate Basic (free, 13 hubs)</p>
                      )}

                      {/* Hub Selector (when plan allows hub selection) */}
                      {selectedPlanId && (
                        <div className="mt-2">
                          <HubSelector
                            selectedPlan={corpPlans.find(p => p.id === selectedPlanId) || { hubs: {} }}
                            initialHubs={selectedHubs}
                            onChange={setSelectedHubs}
                          />
                        </div>
                      )}

                      <div className="h-px bg-white/10" />
                      <p className="text-xs text-slate-500">Admin Account Details</p>
                      <div>
                        <Label className="text-slate-400 text-sm">Admin Name</Label>
                        <Input placeholder="Your name" value={corpData.adminName} onChange={e => setCorpData({...corpData, adminName: e.target.value})}
                          className="bg-slate-800/50 border-white/10 text-white rounded-none mt-1 focus:border-violet-500" required />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm">Admin Email</Label>
                        <Input type="email" placeholder="admin@company.com" value={corpData.adminEmail} onChange={e => setCorpData({...corpData, adminEmail: e.target.value})}
                          className="bg-slate-800/50 border-white/10 text-white rounded-none mt-1 focus:border-violet-500" required />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm">Admin Password</Label>
                        <Input type="password" placeholder="Min 6 characters" value={corpData.adminPassword} onChange={e => setCorpData({...corpData, adminPassword: e.target.value})}
                          className="bg-slate-800/50 border-white/10 text-white rounded-none mt-1 focus:border-violet-500" required minLength={6} />
                      </div>
                      <Button type="submit" disabled={isLoading || corpSubdomainAvailable === false}
                        className="w-full bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700 text-white rounded-none py-5 shadow-lg shadow-violet-500/20 disabled:opacity-50">
                        {isLoading ? "Creating Corporate Account..." : "Register Corporate Account"}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </form>
                    )}
                  </TabsContent>
                </Tabs>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-slate-900 px-4 text-sm text-slate-500">or</span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <section className="relative py-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-['Barlow_Condensed'] text-4xl font-bold text-white mb-4">
                AllBaise capabilities
              </h2>
                <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                  Expandable AI platform for workplace efficiency, document management, team collaboration, and organizational excellence.
                </p>
            </div>
          </div>
        </section>

        {/* Hubs Grid */}
        <section className="relative py-24 bg-slate-900/50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
                  <h2 className="font-['Barlow_Condensed'] text-4xl font-bold text-white mb-4">
                  9 INTEGRATED HUBS
                </h2>
              <p className="text-slate-400 text-lg"></p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {hubs.map((hub, idx) => (
                <div 
                  key={idx}
                  className="group relative p-6 bg-slate-800/30 border border-white/5 hover:border-white/20 transition-all cursor-pointer overflow-hidden"
                >
                  <div className={`absolute inset-0 ${hubStyles[hub.color].panel} opacity-0 group-hover:opacity-100 transition-opacity`} />
                  <div className="relative">
                    <div className={`w-10 h-10 mb-4 flex items-center justify-center ${hubStyles[hub.color].panel}`}>
                      <hub.icon className={`w-5 h-5 ${hubStyles[hub.color].icon}`} />
                    </div>
                    <h3 className="font-semibold text-white mb-1">{hub.name}</h3>
                    <p className="text-sm text-slate-500">{hub.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Super Bot Section */}
        <section className="relative py-24">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm mb-6">
                  <Bot className="w-4 h-4" />
                  AGENT SUPPORT
                </div>
                <h2 className="font-['Barlow_Condensed'] text-4xl font-bold text-white mb-6">
                  SUPER BOT WITH <br />
                  <span className="bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">
                    CROSS-HUB KNOWLEDGE
                  </span>
                </h2>
                <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                  One AI assistant that knows everything across all 9 hubs.
                  Ask about documents, surveillance alerts, HR data, tasks, 
                  innovation ideas, rewards, or media intelligence - all in one conversation.
                </p>
                <ul className="space-y-3">
                  {["Document analysis & transcriptions", "Surveillance alerts & monitoring", "HR performance & skills data", "Rewards & credits insights"].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-300">
                      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <div className="absolute -inset-8 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 blur-3xl" />
                <div className="relative bg-slate-900/80 border border-white/10 p-6">
                  <div className="flex items-center gap-3 border-b border-white/10 pb-4 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Agent Support</p>
                      <p className="text-xs text-slate-500">GPT-5.2 • All Hubs Connected</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-800/50 p-3 rounded-none border-l-2 border-blue-500">
                      <p className="text-sm text-slate-300">What are the critical alerts and pending tasks for today?</p>
                    </div>
                    <div className="bg-emerald-500/10 p-3 rounded-none border-l-2 border-emerald-500">
                      <p className="text-sm text-slate-300">
                        <span className="text-emerald-400 font-semibold">Agent:</span> You have 3 critical alerts in Monitoring Centre: 
                        Border incident (high), suspicious vehicle detected. Plus 5 pending tasks due today...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/5 py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-600 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <span className="font-['Barlow_Condensed'] text-lg font-bold text-white">
                  AllBaise
                </span>
              </div>
              <p className="text-slate-500 text-sm">
                Closing workspace gaps with Innovation and AI
              </p>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <Lock className="w-4 h-4" />
                <span>Enterprise Security</span>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
